import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { alpha } from '@mui/material/styles';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import {
  getCoachInsights,
  getCoachActions,
  getCoachRecommendations,
  completeCoachAction,
  getDocketProgrammes,
  enrolDocketProgramme,
  getDocketAssignments,
  updateDocketAssignment,
  getDocketProgrammeScore,
} from './FinancialService';
import {
  CoachInsight,
  CoachAction,
  CoachRecommendation,
  ProgrammeSummary,
  ProgrammeAssignment,
  ProgrammeScore,
} from './types';
import FeatureGate from './FeatureGate';
import { FeatureKeys } from './FeatureKeys';

const C = {
  ink: '#17263d',
  muted: '#78869a',
  blue: '#1475d1',
  green: '#159b72',
  red: '#ff454d',
  orange: '#ff8b32',
};

// Severity colours aligned with prototype and HomePage
const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '#ff454d',
  HIGH: '#ff8b32',
  MEDIUM: '#1475d1',
  LOW: '#159b72',
};

const severityChip = (severity: string) => (
  <Chip
    label={severity.charAt(0) + severity.slice(1).toLowerCase()}
    size="small"
    sx={{
      height: 20,
      fontSize: '0.65rem',
      fontWeight: 700,
      bgcolor: alpha(SEVERITY_COLORS[severity] ?? C.muted, 0.12),
      color: SEVERITY_COLORS[severity] ?? C.muted,
    }}
  />
);

const gbp = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(n);

// ─── Insights panel ───────────────────────────────────────────────────────────

interface InsightsPanelProps {
  insights: CoachInsight[];
  loading: boolean;
}

const InsightsPanel = ({ insights, loading }: InsightsPanelProps) => {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} key={i}>
            <Skeleton
              variant="rectangular"
              height={120}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (insights.length === 0) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <LightbulbOutlinedIcon
            sx={{ fontSize: 52, color: 'text.disabled', mb: 1.5 }}
          />
          <Typography
            variant="h6"
            fontWeight={600}
            gutterBottom
            sx={{ color: C.ink }}
          >
            No insights yet
          </Typography>
          <Typography variant="body2" sx={{ color: C.muted }}>
            Add some transactions so Docket can analyse your spending and
            surface recommendations.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      {insights.map((insight) => {
        const color = SEVERITY_COLORS[insight.severity] ?? C.muted;
        return (
          <Grid item xs={12} key={insight.id}>
            <Card sx={{ borderLeft: `4px solid ${color}` }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: C.ink }}
                  >
                    {insight.title}
                  </Typography>
                  {severityChip(insight.severity)}
                </Box>
                <Typography variant="body2" sx={{ color: C.muted, mb: 1 }}>
                  {insight.summary}
                </Typography>
                {insight.evidence && (
                  <Typography variant="caption" color="text.disabled">
                    {insight.evidence}
                  </Typography>
                )}
                {insight.category && (
                  <Box sx={{ mt: 1.5 }}>
                    <Chip
                      label={insight.category.toLowerCase().replace('_', ' ')}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.68rem', textTransform: 'capitalize' }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

// ─── Actions panel ────────────────────────────────────────────────────────────

interface ActionsPanelProps {
  actions: CoachAction[];
  loading: boolean;
  onComplete: (key: string) => void;
}

const ActionsPanel = ({ actions, loading, onComplete }: ActionsPanelProps) => {
  const [completing, setCompleting] = useState<string | null>(null);

  const handleComplete = async (action: CoachAction) => {
    setCompleting(action.apiKey);
    await onComplete(action.apiKey);
    setCompleting(null);
  };

  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2].map((i) => (
          <Grid item xs={12} key={i}>
            <Skeleton
              variant="rectangular"
              height={140}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (actions.length === 0) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 52, color: 'text.disabled', mb: 1.5 }}
          />
          <Typography
            variant="h6"
            fontWeight={600}
            gutterBottom
            sx={{ color: C.ink }}
          >
            All caught up
          </Typography>
          <Typography variant="body2" sx={{ color: C.muted }}>
            No active actions right now. Check back after adding more
            transactions.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const active = actions.filter((a) => a.status === 'ACTIVE');
  const completed = actions.filter((a) => a.status === 'COMPLETED');

  return (
    <Box>
      {active.length > 0 && (
        <>
          <Typography
            variant="overline"
            sx={{ color: C.muted, mb: 1, display: 'block' }}
          >
            Active ({active.length})
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {active.map((action) => (
              <Grid item xs={12} key={action.id}>
                <Card>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ color: C.ink }}
                      >
                        {action.actionTitle}
                      </Typography>
                      {action.targetSaving > 0 && (
                        <Chip
                          label={`Save ${gbp(action.targetSaving)}/mo`}
                          size="small"
                          sx={{
                            bgcolor: alpha(C.green, 0.1),
                            color: C.green,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: C.muted, mb: 1.5 }}
                    >
                      {action.actionDescription}
                    </Typography>
                    {action.affectedAreas &&
                      action.affectedAreas.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.75,
                          flexWrap: 'wrap',
                          mb: 2,
                        }}
                      >
                        {action.affectedAreas.map((area) => (
                          <Chip
                            key={area}
                            label={area}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.68rem',
                              textTransform: 'capitalize',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CheckCircleOutlineIcon />}
                      disabled={completing === action.apiKey}
                      onClick={() => handleComplete(action)}
                      sx={{
                        bgcolor: C.green,
                        '&:hover': { bgcolor: alpha(C.green, 0.85) },
                      }}
                    >
                      {completing === action.apiKey
                        ? 'Saving…'
                        : 'Mark complete'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {completed.length > 0 && (
        <>
          <Typography
            variant="overline"
            sx={{ color: C.muted, mb: 1, display: 'block' }}
          >
            Completed ({completed.length})
          </Typography>
          <Grid container spacing={2}>
            {completed.map((action) => (
              <Grid item xs={12} key={action.id}>
                <Card sx={{ opacity: 0.6 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleOutlineIcon
                        sx={{ color: C.green, fontSize: 18 }}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ color: C.ink }}
                      >
                        {action.actionTitle}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

// ─── Recommendations panel ────────────────────────────────────────────────────

interface RecommendationsPanelProps {
  recommendations: CoachRecommendation[];
  loading: boolean;
}

const RecommendationsPanel = ({
  recommendations,
  loading,
}: RecommendationsPanelProps) => {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Skeleton
              variant="rectangular"
              height={120}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (recommendations.length === 0) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <TrendingUpIcon
            sx={{ fontSize: 52, color: 'text.disabled', mb: 1.5 }}
          />
          <Typography
            variant="h6"
            fontWeight={600}
            gutterBottom
            sx={{ color: C.ink }}
          >
            No recommendations yet
          </Typography>
          <Typography variant="body2" sx={{ color: C.muted }}>
            Recommendations will appear as you use Docket.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      {recommendations.map((rec) => (
        <Grid item xs={12} sm={6} key={rec.id}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: C.ink }}
                >
                  {rec.title}
                </Typography>
                {rec.category && (
                  <Chip
                    label={rec.category.toLowerCase().replace('_', ' ')}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', textTransform: 'capitalize' }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ color: C.muted, mb: 1.5 }}>
                {rec.description}
              </Typography>
              {rec.actionLabel && (
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: C.blue }}
                >
                  {rec.actionLabel} →
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// ─── Programme detail dialog ──────────────────────────────────────────────────


interface ProgrammeDetailDialogProps {
  programme: ProgrammeSummary | null;
  onClose: () => void;
  onEnrol: (code: string) => void;
}

const ProgrammeDetailDialog = ({
  programme,
  onClose,
  onEnrol,
}: ProgrammeDetailDialogProps) => {
  const [assignments, setAssignments] = useState<ProgrammeAssignment[]>([]);
  const [score, setScore] = useState<ProgrammeScore | null>(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState<number | null>(null);
  const [error, setError] = useState('');

  const enrolled = !!programme?.status;

  useEffect(() => {
    if (!programme) return;
    setAssignments([]);
    setScore(null);
    setError('');
    if (!enrolled) return;

    setLoading(true);
    Promise.all([
      getDocketAssignments(programme.programmeCode),
      getDocketProgrammeScore(programme.programmeCode),
    ])
      .then(([aRes, sRes]) => {
        setAssignments(aRes.data);
        setScore(sRes.data);
      })
      .catch(() => setError('Could not load programme details.'))
      .finally(() => setLoading(false));
  }, [programme, enrolled]);

  const handleComplete = async (assignment: ProgrammeAssignment) => {
    if (!assignment.id) return;
    setCompleting(assignment.id);
    try {
      const res = await updateDocketAssignment(assignment.id, {
        status: 'COMPLETED',
      });
      setAssignments((prev) =>
        prev.map((a) => (a.id === assignment.id ? res.data : a)),
      );
      // Refresh score
      if (programme) {
        const sRes = await getDocketProgrammeScore(programme.programmeCode);
        setScore(sRes.data);
      }
    } catch {
      setError('Could not update assignment.');
    } finally {
      setCompleting(null);
    }
  };

  if (!programme) return null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: C.ink }}>
            {programme.displayName}
          </Typography>
          {score && (
            <Typography variant="caption" sx={{ color: C.muted }}>
              {score.completedAssignments}/{score.totalAssignments} steps ·{' '}
              {score.progressPercentage}% complete
              {score.grade ? ` · Grade ${score.grade}` : ''}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!enrolled && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" sx={{ color: C.muted, mb: 2 }}>
              You are not enrolled in this programme yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<PlayArrowOutlinedIcon />}
              onClick={() => onEnrol(programme.programmeCode)}
              sx={{ bgcolor: C.blue, '&:hover': { bgcolor: alpha(C.blue, 0.85) } }}
            >
              Start programme
            </Button>
          </Box>
        )}

        {enrolled && (
          <>
            {/* Progress bar */}
            <Box sx={{ mb: 2.5 }}>
              <LinearProgress
                variant="determinate"
                value={programme.progressPercentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: alpha(C.blue, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: C.blue, borderRadius: 4 },
                }}
              />
            </Box>

            {loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 1.5 }} />
                ))}
              </Box>
            )}

            {!loading && assignments.map((a, idx) => {
              const done = a.status === 'COMPLETED';
              const inProgress = a.status === 'IN_PROGRESS';
              const locked = a.locked;

              return (
                <Box key={a.id ?? idx}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 1.5,
                      opacity: locked ? 0.45 : 1,
                    }}
                  >
                    {/* Step icon */}
                    <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                      {done ? (
                        <CheckCircleIcon sx={{ color: C.green, fontSize: 22 }} />
                      ) : locked ? (
                        <LockOutlinedIcon sx={{ color: C.muted, fontSize: 20 }} />
                      ) : (
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            border: `2px solid ${C.blue}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: C.blue, fontWeight: 700, fontSize: '0.6rem' }}
                          >
                            {a.sequenceOrder}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Step content */}
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={inProgress ? 700 : 600}
                          sx={{
                            color: done ? C.muted : C.ink,
                            textDecoration: done ? 'line-through' : 'none',
                          }}
                        >
                          {a.title}
                        </Typography>
                        {inProgress && !done && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={completing === a.id}
                            onClick={() => handleComplete(a)}
                            sx={{
                              minWidth: 90,
                              fontSize: '0.7rem',
                              borderColor: C.green,
                              color: C.green,
                              flexShrink: 0,
                              '&:hover': {
                                borderColor: C.green,
                                bgcolor: alpha(C.green, 0.06),
                              },
                            }}
                          >
                            {completing === a.id ? 'Saving…' : 'Mark done'}
                          </Button>
                        )}
                      </Box>
                      {a.description && (
                        <Typography
                          variant="caption"
                          sx={{ color: C.muted, display: 'block', mt: 0.25 }}
                        >
                          {a.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {idx < assignments.length - 1 && (
                    <Divider sx={{ ml: 4.5, opacity: 0.5 }} />
                  )}
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Programmes panel ─────────────────────────────────────────────────────────

interface ProgrammesPanelProps {
  programmes: ProgrammeSummary[];
  loading: boolean;
  onOpen: (p: ProgrammeSummary) => void;
  onEnrol: (code: string) => void;
}

const ProgrammesPanel = ({
  programmes,
  loading,
  onOpen,
  onEnrol,
}: ProgrammesPanelProps) => {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} key={i}>
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (programmes.length === 0) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <TrendingUpIcon sx={{ fontSize: 52, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: C.ink }}>
            No programmes yet
          </Typography>
          <Typography variant="body2" sx={{ color: C.muted }}>
            Financial programmes are not available on your current plan.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      {programmes.map((p) => {
        const enrolled = !!p.status;
        const done = p.status === 'COMPLETED';

        return (
          <Grid item xs={12} key={p.programmeCode}>
            <Card
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
              onClick={() => onOpen(p)}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: enrolled ? 1.5 : 0,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: C.ink }}>
                      {p.displayName}
                    </Typography>
                    {enrolled && (
                      <Typography variant="caption" sx={{ color: C.muted }}>
                        {p.completedAssignments}/{p.totalAssignments} steps
                      </Typography>
                    )}
                  </Box>
                  {done ? (
                    <Chip
                      label="Completed"
                      size="small"
                      icon={<CheckCircleOutlineIcon />}
                      sx={{
                        bgcolor: alpha(C.green, 0.1),
                        color: C.green,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                      }}
                    />
                  ) : enrolled ? (
                    <Chip
                      label="In progress"
                      size="small"
                      sx={{
                        bgcolor: alpha(C.blue, 0.1),
                        color: C.blue,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                      }}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEnrol(p.programmeCode);
                      }}
                      sx={{
                        fontSize: '0.7rem',
                        borderColor: C.blue,
                        color: C.blue,
                        '&:hover': { bgcolor: alpha(C.blue, 0.06), borderColor: C.blue },
                      }}
                    >
                      Start
                    </Button>
                  )}
                </Box>

                {enrolled && (
                  <LinearProgress
                    variant="determinate"
                    value={p.progressPercentage}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(C.blue, 0.12),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: done ? C.green : C.blue,
                        borderRadius: 3,
                      },
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

// ─── CoachPage ────────────────────────────────────────────────────────────────

const CoachPage = (): JSX.Element => {
  const [tab, setTab] = useState(0);

  const [insights, setInsights] = useState<CoachInsight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insightsError, setInsightsError] = useState('');

  const [actions, setActions] = useState<CoachAction[]>([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [actionsError, setActionsError] = useState('');

  const [recommendations, setRecommendations] = useState<CoachRecommendation[]>([]);
  const [recsLoading, setRecsLoading] = useState(true);
  const [recsError, setRecsError] = useState('');

  const [programmes, setProgrammes] = useState<ProgrammeSummary[]>([]);
  const [progsLoading, setProgsLoading] = useState(true);
  const [progsError, setProgsError] = useState('');
  const [selectedProg, setSelectedProg] = useState<ProgrammeSummary | null>(null);

  useEffect(() => {
    getCoachInsights()
      .then((res) => setInsights(res.data))
      .catch(() => setInsightsError('Could not load insights.'))
      .finally(() => setInsightsLoading(false));

    getCoachActions()
      .then((res) => setActions(res.data))
      .catch(() => setActionsError('Could not load actions.'))
      .finally(() => setActionsLoading(false));

    getCoachRecommendations()
      .then((res) => setRecommendations(res.data))
      .catch(() => setRecsError('Could not load recommendations.'))
      .finally(() => setRecsLoading(false));

    getDocketProgrammes()
      .then((res) => setProgrammes(res.data))
      .catch(() => setProgsError('Could not load programmes.'))
      .finally(() => setProgsLoading(false));
  }, []);

  const handleComplete = async (key: string) => {
    setActions((prev) =>
      prev.map((a) =>
        a.apiKey === key ? { ...a, status: 'COMPLETED' as const } : a,
      ),
    );
    try {
      await completeCoachAction(key);
    } catch {
      setActions((prev) =>
        prev.map((a) =>
          a.apiKey === key ? { ...a, status: 'ACTIVE' as const } : a,
        ),
      );
    }
  };

  const handleEnrol = async (code: string) => {
    try {
      const res = await enrolDocketProgramme(code);
      setProgrammes((prev) => {
        const idx = prev.findIndex((p) => p.programmeCode === code);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = res.data;
          return next;
        }
        return [...prev, res.data];
      });
      setSelectedProg(res.data);
    } catch {
      setProgsError('Could not enrol in programme.');
    }
  };

  const activeActionCount = actions.filter((a) => a.status === 'ACTIVE').length;

  return (
    <UserDashboard>
      <FeatureGate featureKey={FeatureKeys.MONEY_COACH}>
        <Box
          sx={{
            background: `linear-gradient(175deg, ${alpha(C.blue, 0.05)} 0%, transparent 40%)`,
            minHeight: '100vh',
            py: 4,
          }}
        >
          <Container>
            {/* Header */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}
            >
              <LightbulbOutlinedIcon sx={{ fontSize: 32, color: C.muted }} />
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
                  Coach
                </Typography>
                <Typography variant="body2" sx={{ color: C.muted }}>
                  Personalised insights and recommendations based on your
                  spending
                </Typography>
              </Box>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label="Insights" />
                <Tab
                  label={
                    activeActionCount > 0
                      ? `Actions (${activeActionCount})`
                      : 'Actions'
                  }
                />
                <Tab label="Recommendations" />
                <Tab label="Programmes" />
              </Tabs>
            </Box>

            {tab === 0 && (
              <>
                {insightsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {insightsError}
                  </Alert>
                )}
                <InsightsPanel insights={insights} loading={insightsLoading} />
              </>
            )}
            {tab === 1 && (
              <>
                {actionsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {actionsError}
                  </Alert>
                )}
                <ActionsPanel
                  actions={actions}
                  loading={actionsLoading}
                  onComplete={handleComplete}
                />
              </>
            )}
            {tab === 2 && (
              <>
                {recsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {recsError}
                  </Alert>
                )}
                <RecommendationsPanel
                  recommendations={recommendations}
                  loading={recsLoading}
                />
              </>
            )}
            {tab === 3 && (
              <>
                {progsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {progsError}
                  </Alert>
                )}
                <ProgrammesPanel
                  programmes={programmes}
                  loading={progsLoading}
                  onOpen={setSelectedProg}
                  onEnrol={handleEnrol}
                />
              </>
            )}

            {selectedProg && (
              <ProgrammeDetailDialog
                programme={selectedProg}
                onClose={() => setSelectedProg(null)}
                onEnrol={handleEnrol}
              />
            )}
          </Container>
        </Box>
      </FeatureGate>
    </UserDashboard>
  );
};

export default CoachPage;
